/* Black Friday gift rules / 黑五赠品纯规则。
 * No price mutation or discount execution / 不改价，不执行折扣。
 * Frontend state is not authorization / 前端结果不能授权免费赠品。
 */
(function (root, factory) {
  const rules = factory();
  if (typeof module === "object" && module.exports) module.exports = rules;
  else root.LRBlackFridayGiftRules = rules;
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  "use strict";
  const CAMPAIGN_ID = "lr-black-friday-2026";
  const GIFT_PROPERTY = "_lr_bf_gift";
  const MAX_GIFTS = 5;
  const integer = (value, name) => {
    if (value === null || value === undefined || value === "") throw new TypeError(name);
    const number = Number(value);
    if (!Number.isSafeInteger(number) || number < 0) throw new TypeError(name);
    return number;
  };
  const isGift = (item, dedicatedGiftVariantIds = []) =>
    item.properties?.[GIFT_PROPERTY] === CAMPAIGN_ID ||
    dedicatedGiftVariantIds.some((id) => String(id) === String(item.variant_id));

  function effectiveTiers(configuredTiers) {
    if (!Array.isArray(configuredTiers) || configuredTiers.length > 6 || !configuredTiers.length) throw new TypeError('Invalid tiers / 档位配置无效');
    let previous = 0, giftCount = 0, discountType = 'percentage', discountValue = 0;
    return configuredTiers.filter(tier => tier.enabled !== false).map(tier => {
      const thresholdCents = integer(tier.thresholdCents, 'Invalid threshold / 门槛无效');
      if (thresholdCents <= previous) throw new TypeError('Invalid tier order / 档位顺序无效');
      previous = thresholdCents;
      const rewardType = tier.rewardType || 'both';
      if (!['discount', 'gift', 'both'].includes(rewardType)) throw new TypeError('Invalid reward type / 奖励类型无效');
      if (rewardType !== 'gift') {
        discountType = tier.discountType || 'percentage';
        discountValue = Number(tier.discountValue ?? tier.discountPercent);
        if (!['percentage', 'fixed_amount'].includes(discountType) || !Number.isFinite(discountValue) || discountValue <= 0 ||
          !Number.isSafeInteger(Math.round(discountValue * 100)) || Math.abs(discountValue * 100 - Math.round(discountValue * 100)) > 1e-6 ||
          discountType === 'percentage' && discountValue > 100) throw new TypeError('Invalid discount / 折扣数值无效');
      }
      if (rewardType !== 'discount') {
        const quantity = integer(tier.giftCount, 'Invalid gift count / 赠品件数无效');
        if (quantity < giftCount || quantity < 1 || quantity > MAX_GIFTS) throw new TypeError('Invalid gift count / 赠品总件数必须递增且不超过5');
        giftCount = quantity;
      }
      return { ...tier, enabled: true, rewardType, thresholdCents, giftCount, discountType, discountValue,
        discountPercent: discountType === 'percentage' ? discountValue : 0 };
    });
  }

  function rewardState(subtotalCents, configuredTiers) {
    const subtotal = integer(subtotalCents, "Invalid subtotal / 小计无效");
    if (configuredTiers?.some(tier => tier.rewardType)) {
      const tiers = effectiveTiers(configuredTiers);
      if (!tiers.length) throw new TypeError('No enabled tiers / 至少启用一个节点');
      const current = tiers.filter(tier => subtotal >= tier.thresholdCents).at(-1);
      const nextThresholdCents = tiers.find(tier => subtotal < tier.thresholdCents)?.thresholdCents ?? null;
      return Object.freeze({ subtotalCents: subtotal, giftCount: current?.giftCount || 0,
        discountPercent: current?.discountPercent || 0, discountType: current?.discountType || 'percentage',
        discountValue: current?.discountValue || 0, nextThresholdCents,
        remainingCents: nextThresholdCents === null ? 0 : nextThresholdCents - subtotal,
        allUnlocked: nextThresholdCents === null });
    }
    const tiers = configuredTiers ?? [
      { thresholdCents: 6000, giftCount: 0, discountPercent: 6 },
      { thresholdCents: 12000, giftCount: 1, discountPercent: 8 },
      ...[18000, 27000, 36000, 45000].map((thresholdCents, index) =>
        ({ thresholdCents, giftCount: index + 2, discountPercent: 10 })),
    ];
    if (!Array.isArray(tiers) || tiers.length !== 6) throw new TypeError("Invalid tiers / 档位配置无效");
    let previous = 0;
    tiers.forEach((tier, index) => {
      const threshold = integer(tier.thresholdCents, "Invalid threshold / 门槛无效");
      const percent = integer(tier.discountPercent, "Invalid percentage / 百分比无效");
      if (threshold <= previous || tier.giftCount !== index || percent > 100) {
        throw new TypeError("Invalid tier order / 档位顺序无效");
      }
      previous = threshold;
    });
    const unlocked = tiers.filter((tier) => subtotal >= tier.thresholdCents).at(-1);
    const giftCount = unlocked?.giftCount ?? 0;
    const discountPercent = unlocked?.discountPercent ?? 0;
    const nextThresholdCents = tiers.find((tier) => subtotal < tier.thresholdCents)?.thresholdCents ?? null;
    return Object.freeze({
      subtotalCents: subtotal, giftCount, discountPercent, nextThresholdCents,
      remainingCents: nextThresholdCents === null ? 0 : nextThresholdCents - subtotal,
      allUnlocked: giftCount === MAX_GIFTS,
    });
  }

  // The existing Ajax Cart original_line_price supplies the single activity subtotal.
  // 复用原购物车的折扣前行金额，将结果同时提供给进度条和赠品资格。
  function activitySubtotal(cart, participantProductIds, excludedGiftProductIds = [], strictGiftVariantIds) {
    if (!Array.isArray(cart?.items) || !Array.isArray(participantProductIds)) {
      throw new TypeError("Invalid cart or participants / 购物车或活动商品配置无效");
    }
    if (cart.currency !== "USD") throw new TypeError("USD required / 仅支持美元购物车");
    if (!Array.isArray(excludedGiftProductIds)) {
      throw new TypeError("Invalid gift exclusions / 赠品排除配置无效");
    }
    const participants = new Set(participantProductIds.map(String));
    const excluded = new Set(excludedGiftProductIds.map(String));
    const subtotal = cart.items.reduce((sum, item) => {
      const giftItem = strictGiftVariantIds ? strictGiftVariantIds.some((id) => String(id) === String(item.variant_id)) : isGift(item);
      if (giftItem || excluded.has(String(item.product_id)) ||
          !participants.has(String(item.product_id))) return sum;
      return sum + integer(item.original_line_price, "Missing original line price / 缺少折扣前行金额");
    }, 0);
    return integer(subtotal, "Subtotal overflow / 小计超出范围");
  }

  // Catalog availability must come from a current authoritative inventory read.
  // 商品可用数量必须来自当前库存读取；缺失库存按不可用处理。
  // selectionOrder is a persisted ordered list of line keys, not customer authorization.
  // selectionOrder 是持久化选择顺序，不是免费资格凭据。
  function reconcile(cart, participantProductIds, catalog, selectionOrder = [], enabled = true,
    identity = {}) {
    const dedicatedIds = identity.dedicatedGiftVariantIds ?? [];
    if (!Array.isArray(dedicatedIds)) {
      throw new TypeError("Invalid gift identity / 赠品身份配置无效");
    }
    const belongsToGift = (item) => identity.strictDedicatedIdentity
      ? dedicatedIds.some((id) => String(id) === String(item.variant_id))
      : isGift(item, dedicatedIds);
    const state = rewardState(activitySubtotal(cart, participantProductIds,
      identity.excludedGiftProductIds ?? [], identity.strictDedicatedIdentity ? dedicatedIds : undefined),
      enabled ? identity.rewardTiers : undefined);
    if (!Array.isArray(catalog) || !Array.isArray(selectionOrder)) {
      throw new TypeError("Invalid gift catalog / 赠品配置无效");
    }
    const availability = new Map(catalog.map((gift) => [
      String(gift.variantId), integer(gift.availableQuantity, "Invalid stock / 库存无效"),
    ]));
    // Paid purchases of the same variant consume stock but not gift allowance.
    // 同变体付费数量消耗库存，不消耗赠品名额。
    for (const item of cart.items) {
      const id = String(item.variant_id);
      if (!belongsToGift(item) && availability.has(id)) {
        availability.set(id, Math.max(0, availability.get(id) -
          integer(item.quantity, "Invalid quantity / 数量无效")));
      }
    }
    const order = new Map(selectionOrder.map((key, index) => [String(key), index]));
    // Use a closure: Array.filter passes an index as its second argument.
    // 使用闭包，避免 filter 的索引参数被误作赠品身份列表。
    const gifts = cart.items.filter(belongsToGift).map((item, index) => ({ item, index }));
    gifts.sort((a, b) => {
      const leftSequence = Number(a.item.properties?._lr_bf_order);
      const rightSequence = Number(b.item.properties?._lr_bf_order);
      if (Number.isSafeInteger(leftSequence) && leftSequence > 0 &&
          Number.isSafeInteger(rightSequence) && rightSequence > 0 && leftSequence !== rightSequence) {
        return leftSequence - rightSequence;
      }
      const left = order.get(String(a.item.key)) ?? selectionOrder.length + a.index;
      const right = order.get(String(b.item.key)) ?? selectionOrder.length + b.index;
      return left - right;
    });
    let remaining = enabled ? state.giftCount : 0;
    const updates = {};
    const kept = [];
    const changes = [];
    for (const { item } of gifts) {
      if (!item.key) throw new TypeError("Missing cart line key / 缺少商品行标识");
      const id = String(item.variant_id);
      const quantity = integer(item.quantity, "Invalid quantity / 数量无效");
      const stock = availability.get(id) ?? 0;
      const allowed = Math.min(quantity, remaining, stock);
      remaining -= allowed;
      if (availability.has(id)) availability.set(id, stock - allowed);
      if (allowed) kept.push({ key: item.key, variantId: id, quantity: allowed });
      if (allowed !== quantity) {
        updates[item.key] = allowed;
        changes.push({ key: item.key, previousQuantity: quantity, quantity: allowed,
          reason: !enabled ? "disabled" : !availability.has(id) ? "removed"
            : stock < quantity ? "stock" : "allowance" });
      }
    }
    return { state, updates, kept, changes,
      selectedCount: kept.reduce((sum, item) => sum + item.quantity, 0),
      remainingSlots: remaining,
      availableGifts: catalog.filter((gift) => (availability.get(String(gift.variantId)) ?? 0) > 0)
        .map((gift) => ({ ...gift, remainingStock: availability.get(String(gift.variantId)) })),
    };
  }

  return Object.freeze({ CAMPAIGN_ID, GIFT_PROPERTY, MAX_GIFTS, isGift,
    rewardState, effectiveTiers, activitySubtotal, reconcile });
});
